import {
  websocket,
  ConnectionMessageType,
  WebsocketMessage,
  SystemMessageType,
  MetricMessageType,
  AuthMessageType,
  ClientType,
  MetricSourceType,
  ConnFastStartPayload,
  ConnBeaconFastStartPayload,
  PeerMetadata,
} from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import { handleAuthMessage } from './handlers/authMessage';
import { handleConnectionMessage } from './handlers/conectionMessage';
import { handleBeaconConnectionMessage } from './handlers/beaconMessage';
import { handleInvitationMessage } from './handlers/invitationMessage';
import { handleLobbyMessage } from './handlers/lobbyMessage';
import { handlePeerMessage } from './handlers/peerMessage';
import { handleProfileMessage } from './handlers/profileMessage';
import { handleUserEventsMessage } from './handlers/userEventsMessage';
import type { WebrtcQualityStatsMetric } from '../../types/webrtcTypes';
import { playerConnectionState } from '../../stores/playerStores';
import { invitationState } from '../../stores/lobbyStores';
import { authStore, joinState } from '../../stores/globalStores';
import { getPersistentPlayerId } from '../..';
import { getPeer } from '../peer';
import { handleMatchmakerMessage } from './handlers/matchmakerMessage';
import { handleOutboundMessage } from './handlers/outboundMessage';
import { stampOutbound } from '../playerObservability';
import { isTouchDevice } from '../../../utility/utility';
import { DeviceMetadataManager } from '../../../features/mobile-stream/managers/DeviceMetadataManager';
import { getGuestDecodeCapability, sampleGuestQuestionableForBuild } from '@playcastdotio/PeerCore';
// Local duplicate of diagnostic writer to avoid cross-package import issues
const recordDisconnectReason = (reason: string, context?: string) => {
  try {
    if (typeof window === 'undefined' || !('localStorage' in window)) return;
    const key = 'playcast.peer.disconnectHistory';
    const now = new Date().toLocaleString();
    const raw = window.localStorage.getItem(key);
    const history: Array<{ at: string; reason: string; context?: string }> = raw ? JSON.parse(raw) : [];
    history.unshift({ at: now, reason, context });
    window.localStorage.setItem(key, JSON.stringify(history.slice(0, 20)));
  } catch (err) {
    console.warn('[Marketing] Failed to record disconnect reason', err);
  }
};

const HEARTBEAT_INTERVAL = 3000; // 3 seconds between pings
const RECONNECT_TIMEOUT = 500; // 500ms before reconnecting

let wsConnection: WebSocket | null = null;
let isReady = false;
const messageQueue: WebsocketMessage[] = [];
let heartbeatInterval: NodeJS.Timeout | null = null;
let reconnectTimeout: NodeJS.Timeout | null = null;
let isReconnecting = false;
/**
 * Live connect-time link measurement: realtime WebSocket handshake RTT (connect() → ready).
 * The connect-time bandwidth probe uses this so a low-bandwidth link can be seated on the
 * low tier before the stream starts, instead of discovering it mid-session.
 */
let wsConnectStartAt = 0;
let wsHandshakeRttMs: number | undefined;
/**
 * Last time the server answered a heartbeat with SYS_PONG. Dead-socket detection is gated
 * on this: only a socket that has PROVEN the server pongs can be judged dead by a missing
 * pong. Against a server or proxy that never pongs, the detector stays inert and we fall
 * back to the browser's own close detection - hard-disconnecting on input silence alone
 * caused a reconnect loop on such deployments.
 */
let lastPongAt = 0;
/** True once the server has replied to at least one heartbeat. */
let pongsSeen = false;
const readyListeners = new Set<() => void>();
let pendingFastStartPayload: ConnFastStartPayload | null = null;
let pendingBeaconFastStartPayload: ConnBeaconFastStartPayload | null = null;
let fastStartInvitationId: string | null = null;

const clearReconnectTimeout = () => {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
};

const startHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }

  heartbeatInterval = setInterval(() => {
    if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN) {
      stopHeartbeat();
      return;
    }

    try {
      const pingMessage = websocket.generators.system(SystemMessageType.SYS_PING, {});
      wsConnection.send(JSON.stringify(stampOutbound(pingMessage)));
      // A dead radio does not close the socket; it just stops answering. Only a socket
      // that has PROVEN the server answers heartbeats (SYS_PONG seen) can be judged dead
      // by a missing pong - against a server or proxy that never pongs, this check stays
      // inert and the browser's own close detection takes over.
      if (pongsSeen && Date.now() - lastPongAt > HEARTBEAT_INTERVAL * 2) {
        console.warn('[Realtime] Dead socket detected - no pong for >2 heartbeat periods');
        disconnect();
        handleReconnect();
        return;
      }
    } catch (error) {
      console.error('[Realtime] Failed to send heartbeat:', error);
      disconnect();
      handleReconnect();
    }
  }, HEARTBEAT_INTERVAL);
};

const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

const handleReconnect = () => {
  // Prevent multiple simultaneous reconnection attempts
  if (isReconnecting) {
    console.log('[Realtime] Reconnection already in progress');
    return;
  }

  isReconnecting = true;

  // Clean up existing connection if it exists
  disconnect();

  // Wait before attempting to reconnect
  clearReconnectTimeout();
  reconnectTimeout = setTimeout(() => {
    try {
      // Reset wsConnection to null before attempting to reconnect
      wsConnection = null;
      connect();
    } catch (error) {
      console.error('[Realtime] Reconnection attempt failed:', error);
    } finally {
      isReconnecting = false;
    }
  }, RECONNECT_TIMEOUT);
};

export const connect = () => {
  if (wsConnection?.readyState === WebSocket.OPEN || wsConnection?.readyState === WebSocket.CONNECTING) {
    console.warn('[Realtime] Connection already exists');
    return;
  }

  try {
    wsConnectStartAt = Date.now();
    const ws = new WebSocket(constants.REALTIME_API_WEBSOCKET_URL);
    wsConnection = ws;
    // A fresh socket has not proven liveness yet: the dead-socket detector stays inert
    // until the server answers a heartbeat with SYS_PONG.
    lastPongAt = 0;
    pongsSeen = false;

    ws.onopen = () => {
      setTimeout(() => {
        if (wsConnection !== ws) return;
        if (ws.readyState === WebSocket.OPEN) {
          if (pendingBeaconFastStartPayload) {
            console.log('[Realtime] Connected, sending CONN_BEACON_FAST_START');
            const msg = websocket.generators.connection(
              ConnectionMessageType.CONN_BEACON_FAST_START,
              pendingBeaconFastStartPayload,
            );
            ws.send(JSON.stringify(stampOutbound(msg)));
            pendingBeaconFastStartPayload = null;
          } else if (pendingFastStartPayload) {
            console.log('[Realtime] Connected, sending CONN_FAST_START');
            const msg = websocket.generators.connection(
              ConnectionMessageType.CONN_FAST_START,
              pendingFastStartPayload,
            );
            ws.send(JSON.stringify(stampOutbound(msg)));
            pendingFastStartPayload = null;
          } else {
            console.log('[Realtime] Connected, sending init message');
            const msg = websocket.generators.connection(ConnectionMessageType.CONN_INIT, {
              version: constants.VERSION,
            });
            ws.send(JSON.stringify(stampOutbound(msg)));
          }
          startHeartbeat();
        } else {
          console.warn('[Realtime] Connection not ready after open event');
          handleReconnect();
        }
      }, 0);
    };

    ws.onmessage = (event) => {
      if (wsConnection !== ws) return;

      const validation = websocket.validateWebsocketMessage(event.data);
      if (!validation.success) {
        console.error('[Realtime] Invalid message:', validation.error);
        return;
      }

      const message = validation.data;
      if (!message) {
        console.error('[Realtime] No message data');
        return;
      }

      try {
        if (websocket.isSystemMessage(message)) {
          if (message.type === SystemMessageType.SYS_PONG) {
            lastPongAt = Date.now();
            pongsSeen = true;
          } else if (message.type === SystemMessageType.SYS_ERROR) {
            if (message.error.code === 'KICKED') {
              console.log('[Realtime] Kicked from lobby');
              try {
                const reason = (message as any)?.error?.message || 'kicked';
                recordDisconnectReason(`lobby:${reason}`, 'marketing-demo:lobby');
              } catch (err) {
                console.warn('[Marketing] Failed to record lobby kick reason', err);
              }
              joinState.updateState({
                ...joinState.currentState,
                state: 'playerKicked',
              });
            }
          }
        } else if (websocket.isConnectionMessage(message)) {
          if (
            message.type === ConnectionMessageType.CONN_BEACON_FAST_START_SUCCESS ||
            message.type === ConnectionMessageType.CONN_BEACON_FAST_START_ERROR
          ) {
            handleBeaconConnectionMessage(message);
          } else {
            handleConnectionMessage(message, ws);
          }
        } else if (websocket.isAuthMessage(message)) {
          handleAuthMessage(message);
        } else if (websocket.isInvitationMessage(message)) {
          handleInvitationMessage(message);
        } else if (websocket.isLobbyMessage(message)) {
          handleLobbyMessage(message);
        } else if (websocket.isPeerMessage(message)) {
          handlePeerMessage(message);
        } else if (websocket.isProfileMessage(message)) {
          handleProfileMessage(message);
        } else if (websocket.isMatchmakingMessage(message)) {
          handleMatchmakerMessage(message);
        } else if (websocket.isOutboundMessage(message)) {
          handleOutboundMessage(message);
        } else if (websocket.isUserEventMessage(message)) {
          handleUserEventsMessage(message);
        }
      } catch (e) {
        console.error('[Realtime] Failed to handle message:', e);
      }
    };

    ws.onclose = () => {
      if (wsConnection !== ws) return;
      console.log('[Realtime] WebSocket closed');
      isReady = false;
      stopHeartbeat();

      if (!isReconnecting) {
        handleReconnect();
      }

      if (joinState.currentState.state === 'inQueue') {
        window.location.reload();
      }
    };

    ws.onerror = (error) => {
      if (wsConnection !== ws) return;
      console.error('[Realtime] WebSocket error:', error);
    };
  } catch (error) {
    console.error('[Realtime] Failed to connect:', error);
    handleReconnect();
  }
};

const processQueue = () => {
  while (messageQueue.length > 0 && isReady && wsConnection?.readyState === WebSocket.OPEN) {
    const message = messageQueue.shift();
    if (message) {
      try {
        wsConnection.send(JSON.stringify(stampOutbound(message)));
      } catch (error) {
        console.error('[Realtime] Failed to send queued message:', error);
        messageQueue.unshift(message); // Put the message back at the start of the queue
        break;
      }
    }
  }
};

export const disconnect = () => {
  stopHeartbeat();
  clearReconnectTimeout();
  isReady = false;

  if (wsConnection) {
    const conn = wsConnection;
    wsConnection = null;

    conn.onopen = null;
    conn.onclose = null;
    conn.onerror = null;
    conn.onmessage = null;

    try {
      conn.close();
    } catch (error) {
      console.warn('[Realtime] Error while closing connection:', error);
    }
  }
};

/** Latest measured realtime-WS handshake RTT (connect() → ready), or undefined before one. */
export const getWsHandshakeRttMs = (): number | undefined => wsHandshakeRttMs;

export const setIsReady = (ready: boolean) => {
  const becameReady = ready && !isReady;
  isReady = ready;
  if (isReady && wsConnectStartAt > 0) {
    wsHandshakeRttMs = Date.now() - wsConnectStartAt;
  }
  if (isReady) {
    processQueue();
    if (becameReady) {
      for (const listener of [...readyListeners]) {
        try {
          listener();
        } catch (err) {
          console.error('[Realtime] onReady listener failed:', err);
        }
      }
    }
  }
};

/** Subscribe to realtime auth-ready. Returns unsubscribe. Fires immediately if already ready. */
export const onReady = (listener: () => void): (() => void) => {
  readyListeners.add(listener);
  if (isReady) {
    try {
      listener();
    } catch (err) {
      console.error('[Realtime] onReady listener failed:', err);
    }
  }
  return () => {
    readyListeners.delete(listener);
  };
};

/** Resolves when realtime is ready (auth success). Rejects on timeout. */
export const waitUntilReady = (timeoutMs = 15_000): Promise<void> => {
  if (isReady) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error(`Realtime not ready within ${timeoutMs}ms`));
    }, timeoutMs);
    const unsubscribe = onReady(() => {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    });
  });
};

/**
 * Realtime-websocket-ready gate (Phase 2 cellular failover).
 *
 * Resolves once the realtime WebSocket is re-established/authenticated on the
 * new (cellular) path. The single coordinated guest reclaim awaits this before
 * issuing its full re-offer, so the re-offer is never sent over a dead socket.
 */
export const realtimeWebsocketReady = (): Promise<void> => waitUntilReady(20_000);

export const sendMessage = (message: WebsocketMessage) => {
  if (!wsConnection || wsConnection.readyState !== WebSocket.OPEN || !isReady) {
    console.warn('[Realtime] Connection not ready, queueing message');
    messageQueue.push(message);
    return;
  }

  try {
    wsConnection.send(JSON.stringify(stampOutbound(message)));
  } catch (error) {
    console.error('[Realtime] Failed to send message:', error);
    messageQueue.push(message);
  }
};

/**
 * Guest network-quality hint for ShadowCam selection (Phase 1/4).
 *
 * Uses the shared build-time rule rather than the initial sampler: this payload is re-sent
 * on every WebSocket reconnect, which after a drop IS a recovery. The initial sample cannot
 * describe one — its inputs are frozen at page load — so it reported a failed mobile link
 * as healthy and the host seated the guest back on the 480p camera. Both the guest and the
 * beacon fast-start payloads are built from here, so they answer this identically.
 */
const sampleConnectionQuestionable = (isMobile: boolean): boolean =>
  sampleGuestQuestionableForBuild({ isMobile });

/**
 * The host picks which ShadowCam to capture from these flags before it answers the
 * SDP offer, and fast start bypasses PEER_GET_ID, so every fast-start payload has to
 * carry them or the host defaults the guest to desktop.
 */
const deviceMetadataManager = new DeviceMetadataManager();
const deviceMetadata = (existing?: PeerMetadata): PeerMetadata => {
  const classification = deviceMetadataManager.classify({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    userAgentDataMobile: (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    touchCapable: isTouchDevice(),
  });
  const sampled: PeerMetadata = { ...classification, signalingGenerationVersion: 1 };
  try {
    sampled.connectionQuestionable = sampleConnectionQuestionable(classification.isMobile);
  } catch { /* Network evidence is optional; device classification is already established. */ }
  try {
    const families = getGuestDecodeCapability();
    if (families) sampled.hardwareDecodeFamilies = families;
  } catch { /* Decoder evidence is optional and independent from network evidence. */ }
  return { ...sampled, ...existing, ...classification };
};

/**
 * Connect using the fast-start flow: combines init + auth + invitation creation + queue join
 * into a single WebSocket message. Falls back to legacy flow if the server rejects it.
 */
export const connectFastStart = (payload: Omit<ConnFastStartPayload, 'version'>) => {
  pendingFastStartPayload = {
    ...payload,
    peerMetadata: deviceMetadata(payload.peerMetadata),
    version: constants.VERSION,
  };
  fastStartInvitationId = null;
  connect();
};

export const connectBeaconFastStart = (payload: Omit<ConnBeaconFastStartPayload, 'version'>) => {
  pendingBeaconFastStartPayload = {
    ...payload,
    peerMetadata: deviceMetadata(payload.peerMetadata),
    version: constants.VERSION,
  };
  connect();
};

export const setFastStartInvitationId = (id: string | null) => {
  fastStartInvitationId = id;
};

export const getFastStartInvitationId = () => fastStartInvitationId;

export const sendMetricPlayerBatchWebrtcStats = (stats: WebrtcQualityStatsMetric[], collectionInterval: number) => {
  console.log('1 Sending webrtc quality stats');
  const message = websocket.generators.metrics(MetricMessageType.METRICS_WEBRTC_QUALITY, {
    payload: {
      name: 'webrtc_quality',
      timestamp: new Date().toISOString(),
      payload: {
        sourceType: MetricSourceType.PLAYER,
        collectionInterval,
        broadcastSessionId: playerConnectionState.currentState.broadcastSessionId,
        environment: constants.ENVIRONMENT,
        inviteId: invitationState.currentState.selectedInvite?.id ?? '',
        persistentId: getPersistentPlayerId(),
        playerAuthId: authStore.currentState.playerAuthId ?? '',
        // getPeer() is the LEGACY peer and is undefined whenever USE_NEW_PEER is true — which
        // it is — so this field was always ''. The store carries the live identity on both
        // paths, and survives a reclaim that rotates the peer id mid-session.
        playerConnectionId:
          playerConnectionState.currentState.playerPeerId || getPeer()?.peerId || '',
        version: constants.VERSION,
        qualityStats: stats,
      },
    },
  });
  console.log('2 Sending webrtc quality stats');
  sendMessage(message);
};
